import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus, Upload } from "lucide-react";
import { CsvImport } from "@/components/CsvImport";
import { usePoll, HealthBadge, PageHeader, Empty, Pill } from "@/components/common";
import { ChannelForm } from "@/components/ChannelForm";
import { Button } from "@/components/ui/button";
import { fmtDur, fmtBps, BACKEND } from "@/lib/api";

function ChannelRow({ c }) {
  const nav = useNavigate();
  const r = c.runtime || {};
  const srcCount = c.sources.filter((s) => s.enabled).length;
  return (
    <tr data-testid={`channel-row-${c.slug}`} onClick={() => nav(`/channels/${c.id}`)} className="border-b border-slate-800/70 hover:bg-slate-800/30 cursor-pointer transition-colors">
      <td className="px-4 py-2.5 max-w-[220px]">
        <HealthBadge health={r.health} status={r.status} testid={`channel-health-${c.slug}`} />
        {r.status !== "on_air" && (r.sources || []).map((x) => x.last_error).find(Boolean) && <div data-testid={`channel-error-${c.slug}`} className="mt-1 font-mono text-[10px] text-red-300/80 truncate" title={(r.sources || []).map((x) => x.last_error).find(Boolean)}>{(r.sources || []).map((x) => x.last_error).find(Boolean)}</div>}
      </td>
      <td className="px-4 py-2.5">
        <div className="text-sm text-slate-100 font-medium">{c.name}</div>
        <div className="font-mono text-[11px] text-slate-500">{c.slug}</div>
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-slate-300">
        {r.active_source ? <>#{r.active_source} <span className="text-slate-500">/ {srcCount}</span> <span className="text-slate-500">{r.active_label}</span></> : <span className="text-slate-500">— / {srcCount}</span>}
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-slate-300">{r.media_sequence ?? "—"}<span className="text-slate-500"> · d{r.disc_seq ?? 0}</span></td>
      <td className={`px-4 py-2.5 font-mono text-xs ${r.segment_age > 12 ? "text-red-400" : "text-slate-300"}`}>{r.segment_age != null ? `${r.segment_age}s` : "—"}</td>
      <td className="px-4 py-2.5 space-x-1">
        {c.type === "news" && <Pill tone="blue">news</Pill>}
        {r.transcoding_audio && <Pill tone="cyan">audio→aac</Pill>}
        {r.frozen && <Pill tone="amber">frozen</Pill>}
        {r.audio_ok === false && <Pill tone="amber">silent</Pill>}
        {r.upstream_kind && <Pill>{r.upstream_kind}</Pill>}
      </td>
      <td data-testid={`channel-cpu-${c.slug}`} className="px-4 py-2.5 font-mono text-xs text-slate-300">{c.cost ? `${c.cost.cpu}%` : "—"}</td>
      <td data-testid={`channel-bw-${c.slug}`} className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{c.cost ? <>↓{fmtBps(c.cost.in_bps)}<br />↑{fmtBps(c.cost.out_bps)}</> : "—"}</td>
      <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{fmtDur(r.on_air_for)}</td>
    </tr>
  );
}

export default function Channels() {
  const { data, reload } = usePoll("/admin/channels", 3000);
  const [open, setOpen] = useState(false);
  const [imp, setImp] = useState(false);
  const counts = (data || []).reduce((a, c) => ({ ...a, [c.runtime?.health]: (a[c.runtime?.health] || 0) + 1 }), {});
  return (
    <div>
      <PageHeader title="Channels" sub="Each channel turns up to five ranked upstream sources into one continuous, clean output."
        right={<>
          <a data-testid="export-channels-csv-btn" href={`${BACKEND}/api/admin/channels-csv/export`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-slate-700 text-sm text-slate-200 hover:bg-slate-800"><Download size={14} /> Export CSV</a>
          <Button data-testid="import-channels-csv-btn" variant="outline" className="border-slate-700" onClick={() => setImp(true)}><Upload size={14} /> Import CSV</Button>
          <Button data-testid="new-channel-btn" onClick={() => setOpen(true)} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"><Plus size={15} /> New channel</Button>
        </>} />
      <div className="flex gap-3 mb-4 font-mono text-xs">
        {[["green", "on primary", "text-emerald-400"], ["amber", "on backup", "text-amber-400"], ["red", "failing / off air", "text-red-400"], ["grey", "stopped", "text-slate-400"]].map(([k, l, t]) => (
          <div key={k} data-testid={`count-${k}`} className="px-3 py-1.5 rounded border border-slate-800 bg-[#0F1722]"><span className={`${t} font-semibold`}>{counts[k] || 0}</span> <span className="text-slate-500">{l}</span></div>
        ))}
      </div>
      {data && data.length === 0 ? <Empty>No channels yet. Create one and add sources, or start with a synthetic source from the Sources lab.</Empty> : (
        <div className="bg-[#0F1722] border border-slate-800 rounded-md overflow-hidden">
          <table className="w-full" data-testid="channels-table">
            <thead><tr className="border-b border-slate-800 text-left text-[10px] uppercase tracking-wider text-slate-500">
              {["Status", "Channel", "Active source", "Media seq", "Seg age", "Flags", "CPU", "In / out", "On air"].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>{(data || []).map((c) => <ChannelRow key={c.id} c={c} />)}</tbody>
          </table>
        </div>
      )}
      <ChannelForm open={open} onOpenChange={setOpen} onSaved={reload} />
      <CsvImport open={imp} onOpenChange={setImp} onDone={reload} />
    </div>
  );
}
